import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';
import ErrorMessage from '../../components/errormessage';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import {
    Heading3,
    Block,
    FlexDiv,
    TextDefault,
    palette,
    TextInput,
    PrimaryOpenInNew,
    BigModal,
    Heading2,
    ThemedButton,
    DarkArrowTooltipIcon,
    CopyToClipboardWrapper,
    TextTiny,
    Tag,
} from '@awarego-ui/components';
import TableClient from '../../components/table/TableClient';
import RequireRole from '../RequireRole';

function APIKeys() {
    const [modalId, setModalId] = useState(0);
    const [modalName, setModalName] = useState('');
    const [modalShowLoader, setModalShowLoader] = useState(false);
    const [isModalOpen, setModalIsOpen] = useState(false);

    const { commonStore, companyStore, store, brandingStore } = useStores();
    const { currentCompany } = store;
    const {
        apiKeys,
        error,
        deletingApiKeys,
        loadingApiKeys,
        keyToShowOnce,
    } = companyStore;
    const { APIhelpUrl } = brandingStore;
    const rowsPerPage = 5;

    useEffect(() => {
        if (currentCompany) companyStore.loadApiKeys(currentCompany.company_id);
    }, [currentCompany, companyStore, apiKeys]);

    const createNew = () => {
        companyStore.createNewApiKey(currentCompany.company_id);
    };

    const saveName = async (id, name) => {
        await handleChangeName(id, name);
        setModalIsOpen(false);
    };

    const handleChangeName = React.useCallback(
        async (id, value) => {
            companyStore.setApiKeyName(currentCompany.company_id, id, value);
        },
        [companyStore, currentCompany]
    );

    const openModal = (id, name) => {
        setModalId(id);
        setModalName(name);
        setModalIsOpen(true);
    };

    const deleteKey = (id) => {
        commonStore.showConfirm(
            'Do you really want to delete this API key?',
            'Delete',
            'Delete API key?',
            () => {
                companyStore.deleteApiKey(id, null, {
                    companyId: currentCompany.company_id,
                });
            }
        );
    };

    const columns = useMemo(() => {
        return [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: observer((x) => {
                    return (
                        <>
                            {x.value && x.value.length > 0 ? (
                                x.value
                            ) : (
                                <Tag text="No Name" />
                            )}{' '}
                        </>
                    );
                }),
            },
            {
                Header: 'Access Key',
                accessor: 'maskedValue',
                Cell: observer((x) => {
                    const display =
                        x.row.original.maskedValue ??
                        x.row.original.value ??
                        '...';
                    return <TextTiny>{display}</TextTiny>;
                }),
                disableGlobalFilter: true,
            },
            {
                Header: '',
                accessor: 'id',
                Cell: observer((x) => (
                    <FlexDiv style={{ justifyContent: 'flex-end' }}>
                        {deletingApiKeys.includes(x.value) ? (
                            <CircularProgress />
                        ) : (
                            buildExtraActionsColumn(x.row.original)
                        )}
                    </FlexDiv>
                )),
                disableGlobalFilter: true,
            },
        ];
    }, [
        commonStore,
        companyStore,
        currentCompany.company_id,
        deletingApiKeys,
    ]);

    const buildExtraActionsColumn = (row) => {
        return (
            <>
                <RequireRole>
                    <DarkArrowTooltipIcon
                        customFunction={() => {
                            openModal(row.id, row.name);
                        }}
                        row={row}
                        title="Edit name"
                        icon={'edit'}
                    ></DarkArrowTooltipIcon>
                    <DarkArrowTooltipIcon
                        customFunction={() => deleteKey(row.id)}
                        row={row}
                        title="Delete API Key"
                        icon={'delete'}
                    ></DarkArrowTooltipIcon>
                </RequireRole>
            </>
        );
    };


    return (
        <FlexDiv>
            <Block flexGrow="9" mr="20">
                <ErrorMessage error={error} />
                <FlexDiv column>
                    <Heading2 mb={8}>API Keys</Heading2>
                    <TextDefault>
                        This API is designed to allow access, creation and
                        modifications of trainings and assessments.
                    </TextDefault>
                    <TextDefault>
                        For information on how to use this API, refer to our{' '}
                        <Link
                            href={APIhelpUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            online documentation
                        </Link>
                        .
                    </TextDefault>
                </FlexDiv>
                <FlexDiv pt="32" pb="32">
                    <ThemedButton
                        onClick={createNew}
                        mode="primary"
                        text="Create API Key"
                    />
                </FlexDiv>
                {loadingApiKeys && <CircularProgress />}
                <TableClient
                    columns={columns}
                    initiallyLoaded={loadingApiKeys === false}
                    data={[...apiKeys]}
                    noItemFoundText={'No existing keys'}
                    hidePagination={apiKeys.length <= rowsPerPage}
                    overflowInitial={true}
                />
            </Block>
            <Block column flexGrow="3">
                <Heading3 color={palette.evergreen.aspargus} mb={24}>
                    Online documentation
                </Heading3>
                <TextDefault>
                    <Link
                        href={APIhelpUrl}
                        target="_blank"
                        rel="noreferrer"
                        color="inherit"
                    >
                        Learn more on how to use our API <PrimaryOpenInNew />
                    </Link>
                </TextDefault>
            </Block>

            <BigModal
                isOpen={isModalOpen}
                width={'500'}
                onRequestClose={() => setModalIsOpen(false)}
                heading="Re-name key"
                modalContent={
                    !modalShowLoader && (
                        <TextInput
                            label={'Name'}
                            type="text"
                            className={'rename-key'}
                            value={modalName}
                            onChange={(e) => setModalName(e.target.value)}
                        />
                    )
                }
                modalFooterContent={
                    <Fragment>
                        <ThemedButton
                            mode="primary"
                            text="Save"
                            onClick={async () => {
                                setModalShowLoader(true);
                                await saveName(modalId, modalName);
                                setModalShowLoader(false);
                            }}
                        />
                        <ThemedButton
                            mode="secondary"
                            onClick={() => {
                                setModalIsOpen(false);
                            }}
                            text="Close"
                        />
                    </Fragment>
                }
            />

            <BigModal
                isOpen={!!keyToShowOnce}
                width={'500'}
                onRequestClose={() => companyStore.clearKeyToShowOnce()}
                heading="Your API key"
                modalContent={
                    keyToShowOnce && (
                        <FlexDiv column>
                            <TextDefault
                                mb={16}
                                style={{
                                    fontWeight: 600,
                                    color: palette.evergreen?.aspargus ||
                                        '#c00',
                                }}
                            >
                                Make sure to copy your API key now. You
                                won&apos;t be able to see it again!
                            </TextDefault>
                            <CopyToClipboardWrapper
                                copyText={keyToShowOnce.value}
                                onCopied={commonStore.copied}
                            >
                                <TextInput
                                    readOnly
                                    value={keyToShowOnce.value}
                                    className="api-key-once-value"
                                    style={{ width: '100%' }}
                                />
                            </CopyToClipboardWrapper>
                        </FlexDiv>
                    )
                }
                modalFooterContent={
                    <ThemedButton
                        mode="primary"
                        text="I have copied the key"
                        onClick={() => companyStore.clearKeyToShowOnce()}
                    />
                }
            />
        </FlexDiv>
    );
}

export default observer(APIKeys);
